import React, { useRef, useEffect, useMemo, useState } from "react"
import { Button, Container } from "react-bootstrap"
import FeatureSwitch from "./FeatureSwitch"
import PricingTable from "./PricingTable"

import Select, { components } from "react-select"
import { CustomSelect } from "./CustomSelect"

function getCollectionLink(i18n, collection) {
  if (!collection) {
    return null
  }
  return (
    <a
      href={`${window.location.origin}/${i18n.locale}/${i18n.routes.collections}/${collection.codename}`}
    >
      {collection.name}
    </a>
  )
}

function getModelLink(i18n, model) {
  if (!model) {
    return null
  }
  return (
    <a
      href={`${window.location.origin}/${i18n.locale}/${i18n.routes.fronts}/${model.codename}`}
    >
      {model.name}
    </a>
  )
}

function PricingBox({
  pricing,
  i18n,
  locale,
  configuratorUrl,
  currencyTags,
  selectArrowIcon,
}) {
  const [kind, setKind] = React.useState()
  const [model, setModel] = React.useState()
  const [selectedFeature, setSelectedFeature] = React.useState(null)
  const [nullFeatureAllowed, setNullFeatureAllowed] = React.useState(true)
  const imageContainerRef = useRef(null)
  const contentContainerRef = useRef(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  useEffect(() => {
    const url = new URL(window.location)
    const urlKind = url.searchParams.get("kind")
    const urlModel = url.searchParams.get("model")
    if (urlModel && urlKind) {
      const variant = pricing.variants.find(
        (v) =>
          v.model.codename === urlModel && v.front_kind.codename === urlKind,
      )
      if (variant) {
        setModel(variant.model_id)
        setKind(variant.front_kind_id)
      }
    }
  }, [window.location.href])

  useEffect(() => {
    handleResize()

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isImageLoaded])

  const handleResize = () => {
    if (imageContainerRef.current && contentContainerRef.current) {
      const height = imageContainerRef.current.offsetHeight
      contentContainerRef.current.style.minHeight = `${height}px`
    }
  }

  const kinds = React.useMemo(
    () =>
      pricing.variants
        .map((v) => ({ value: v.front_kind_id, label: v.front_kind.name }))
        .filter(
          ({ value }, i, self) =>
            self.findIndex((other) => value == other.value) === i,
        ),
    [pricing],
  )

  const models = React.useMemo(
    () =>
      pricing.variants
        .filter((v) => v.front_kind_id === kind)
        .map((v) => ({ value: v.model_id, label: v.model.name }))
        .filter(
          ({ value }, i, self) =>
            self.findIndex((other) => value == other.value) === i,
        ),
    [(pricing, kind)],
  )

  useEffect(() => {
    const modelIds = pricing.variants
      .filter((v) => v.front_kind_id === kind)
      .map((v) => v.model_id)
    if (!modelIds.includes(model)) {
      setModel(modelIds[0])
    }
  }, [pricing, kind])

  const remainingPricings = React.useMemo(
    () =>
      pricing.variants.filter(
        (v) => v.front_kind_id === kind && v.model_id === model,
      ),
    [model, kind, pricing],
  )

  const featuresToShow = React.useMemo(
    () =>
      remainingPricings
        .reduce((acc, pricing) => [...acc, pricing.pricing_feature], [])
        .filter(
          (pricing, index, self) =>
            pricing &&
            self.findIndex(
              (otherPricing) => otherPricing?.id === pricing.id,
            ) === index,
        ),
    [remainingPricings],
  )

  useEffect(() => {
    if (!featuresToShow.some((feature) => feature.id === selectedFeature)) {
      setSelectedFeature(null)
    }
  }, [JSON.stringify(featuresToShow)])

  const filteredByFeatures = React.useMemo(
    () =>
      remainingPricings.filter(
        (pricing) => pricing.pricing_feature_id === selectedFeature,
      ),
    [selectedFeature, remainingPricings],
  )

  useEffect(() => {
    if (
      featuresToShow.length > 0 &&
      !remainingPricings.some((variant) => variant.pricing_feature_id === null)
    ) {
      setNullFeatureAllowed(false)
    } else {
      setNullFeatureAllowed(true)
    }
  }, [filteredByFeatures, JSON.stringify(featuresToShow), remainingPricings])

  useEffect(() => {
    if (selectedFeature === null && !nullFeatureAllowed) {
      setSelectedFeature(featuresToShow[0].id)
    }
  }, [selectedFeature, featuresToShow, nullFeatureAllowed])

  const collections = React.useMemo(() => {
    const variant = filteredByFeatures[0]
    const collections = variant?.collections ?? []

    if (collections.length === 0) {
      return ""
    }
    if (collections.length === 1) {
      return getCollectionLink(i18n, collections[0])
    }

    return collections.map((collection, i, self) => (
      <span key={collection.id}>
        {i !== 0 && (i === self.length - 1 ? ` ${i18n.or} ` : ", ")}
        {getCollectionLink(i18n, collection)}
      </span>
    ))
  }, [filteredByFeatures, locale])

  const link = `${configuratorUrl}?system=${pricing.system?.codename}&model=${
    filteredByFeatures[0]?.model?.codename ?? ""
  }`

  const customKindOrder = [2, 1]
  const orderedKinds = kinds.sort((a, b) => {
    return customKindOrder.indexOf(a.value) - customKindOrder.indexOf(b.value)
  })

  const customModelsOrder = [1, 2, 6, 5, 8, 4]
  const orderedModels = models.sort((a, b) => {
    return (
      customModelsOrder.indexOf(a.value) - customModelsOrder.indexOf(b.value)
    )
  })

  useEffect(() => {
    if (kind === undefined && kinds.length > 0) {
      const k = orderedKinds[0] ?? kinds[0]
      setKind(k.value)
    }
  }, [])

  useEffect(() => {
    if (model === undefined && models.length > 0) {
      const m = orderedModels[0] ?? models[0]
      setKind(m.value)
    }
  }, [])

  return (
    <Container
      id={`pricing-box-${pricing.id}`}
      fluid
      className="pricing-box-wrapper"
    >
      <h2 className="pricing-header">{pricing.name}</h2>
      <div className="pricing-description">
        <div className="pricing-description-content">{pricing.description}</div>
      </div>
      <div className="pricing-box">
        <div className="summary-section">
          <div className="pricing-wrapper" ref={imageContainerRef}>
            <div className="pricing-img-wrapper">
              <img
                src={filteredByFeatures[0]?.illustration}
                className="pricing-img"
                onLoad={() => setIsImageLoaded(true)}
              />
            </div>
            <div className="pricing-collection-box">
              {i18n.collection} {collections} / {i18n.model}{" "}
              {getModelLink(i18n, filteredByFeatures[0]?.model)}
            </div>
          </div>
        </div>
        <div className="aside-wrapper">
          <div ref={contentContainerRef} className="content-container">
            <div className="config-section">
              <div className="select-wrapper">
                <div>{i18n.select_front_kind}</div>
                <CustomSelect
                  value={kind}
                  onChange={setKind}
                  options={orderedKinds}
                  selectArrowIcon={selectArrowIcon}
                />
              </div>
              <div className="select-wrapper">
                <div>{i18n.select_front_model}</div>
                <CustomSelect
                  value={model}
                  onChange={setModel}
                  options={orderedModels}
                  selectArrowIcon={selectArrowIcon}
                />
              </div>
              {featuresToShow.map((feature) => (
                <FeatureSwitch
                  key={feature}
                  featureName={feature.name}
                  featureId={feature.id}
                  selectedFeature={selectedFeature}
                  setSelectedFeature={setSelectedFeature}
                  nullFeatureAllowed={nullFeatureAllowed}
                />
              ))}
            </div>
            <PricingTable
              locale={locale}
              i18n={i18n}
              variants={filteredByFeatures}
              currencyTags={currencyTags}
            />
          </div>
          <div className="button-wrapper button-wrapper-desktop">
            <a href={link} className="configurator-button">
              {i18n.price_your_kitchen}
            </a>
          </div>
        </div>
        <div className="button-wrapper button-wrapper-mobile">
          <a href={link} className="configurator-button">
            {i18n.price_your_kitchen}
          </a>
        </div>
      </div>
    </Container>
  )
}

export default PricingBox
